/**
 * Reference planes: detection and coverage rasters (docs/stage-1/PHYSICS.md §8.1).
 *
 * A copper layer is a plane when one net's filled zones cover at least 15 % of the board
 * area on that layer (that net names the plane). Copper of other nets with large pours on the
 * same layer (split planes: GND and 3V3 side by side) joins the coverage raster, because for
 * AC return currents any solid copper is a reference. Each plane is rasterised (scanline, cell 0.1–0.25 mm) so that
 * "is there copper under this point?" is an O(1) lookup.
 */
import { signedArea } from './geometry';
import type { BoardModel, Vec2 } from './types';

export interface CoverageRaster {
  x0: number;
  y0: number;
  cell: number;
  nx: number;
  ny: number;
  data: Uint8Array;
}

export interface PlaneLayer {
  layer: number;
  net: number;
  /** World height of the plane (mm). */
  y: number;
  /** Covered fraction of the board area. */
  coverage: number;
  raster: CoverageRaster;
  /** True when set by the user rather than detected. */
  override: boolean;
  /**
   * Split planes (several nets on the layer): the net of each raster cell, 0 where there is no
   * copper. A line crossing from one net's area into another's needs its return to jump
   * between nets there.
   */
  nets?: Int32Array;
}

/** Net of the plane copper at a point (0: none, or no split information). */
export function netAt(p: PlaneLayer, x: number, y: number): number {
  const r = p.raster;
  if (!p.nets) return covered(r, x, y) ? p.net : 0;
  const i = Math.floor((x - r.x0) / r.cell);
  const j = Math.floor((y - r.y0) / r.cell);
  if (i < 0 || j < 0 || i >= r.nx || j >= r.ny) return 0;
  return p.nets[j * r.nx + i]!;
}

/** Overrides by layer name: a net name forces a plane, null removes one. */
export type PlaneOverrides = Record<string, string | null>;

export function boardArea(board: BoardModel): number {
  const [outer, ...holes] = board.outline;
  if (!outer) return 0;
  return Math.abs(signedArea(outer)) - holes.reduce((s, h) => s + Math.abs(signedArea(h)), 0);
}

/**
 * Holes in a plane up to this size (largest extent, mm) count as copper: anti-pads of vias and
 * through-hole pins. A slot counts by its length, not its area: 0.3 × 9 mm forces the return
 * 4.5 mm aside although it is under 3 mm².
 */
export const HOLE_EXTENT = 2.5;

export function detectPlanes(board: BoardModel, overrides: PlaneOverrides = {}, threshold = 0.15, splitShare = 0.05): PlaneLayer[] {
  const area = boardArea(board) || 1;
  const planes: PlaneLayer[] = [];
  for (const layer of board.layers) {
    const byNet = new Map<number, number>();
    for (const z of board.zones) if (z.layer === layer.index && z.net > 0) byNet.set(z.net, (byNet.get(z.net) ?? 0) + z.area);

    let net = -1;
    let override = false;
    if (layer.name in overrides) {
      const name = overrides[layer.name];
      if (name === null || name === undefined) continue;
      net = board.nets.indexOf(name);
      override = true;
      if (net < 0) continue;
    } else {
      let best = 0;
      for (const [n, a] of byNet) {
        if (a > best) {
          best = a;
          net = n;
        }
      }
      if (net < 0 || best / area < threshold) continue;
    }
    // the plane net plus other large pours on this layer (not for explicit overrides)
    const nets = new Set([net]);
    if (!override) for (const [n, a] of byNet) if (a / area >= splitShare) nets.add(n);
    const polys = board.zones.filter((z) => z.layer === layer.index && nets.has(z.net)).flatMap((z) => z.polygons);
    const raster = fillSmallHoles(rasterize(polys, board.bbox, area), HOLE_EXTENT);
    // which net each cell belongs to, when the layer is split between nets
    let netData: Int32Array | undefined;
    const layerNets = new Set(board.zones.filter((z) => z.layer === layer.index && z.net > 0).map((z) => z.net));
    if (layerNets.size > 1 && !override) {
      netData = new Int32Array(raster.data.length);
      for (const n of layerNets) {
        const r = rasterize(board.zones.filter((z) => z.layer === layer.index && z.net === n).flatMap((z) => z.polygons), board.bbox, area);
        for (let k = 0; k < r.data.length; k++) if (r.data[k]) netData[k] = n;
      }
    }
    planes.push({
      layer: layer.index,
      net,
      y: layer.y,
      coverage: (byNet.get(net) ?? 0) / area,
      raster,
      override,
      ...(netData ? { nets: netData } : {}),
    });
  }
  return planes;
}

/**
 * Scanline fill of keyholed rings (each ring even-odd on its own, then OR-ed). Edges drop
 * their crossings straight into per-row buckets, so the cost is O(edges + crossings) rather
 * than O(rows × edges).
 */
export function rasterize(rings: Vec2[][], bbox: { x0: number; y0: number; x1: number; y1: number }, area: number): CoverageRaster {
  const cell = Math.min(0.25, Math.max(0.1, Math.sqrt(Math.max(area, 1) / 2e6)));
  const x0 = bbox.x0 - cell;
  const y0 = bbox.y0 - cell;
  const nx = Math.ceil((bbox.x1 - bbox.x0) / cell) + 2;
  const ny = Math.ceil((bbox.y1 - bbox.y0) / cell) + 2;
  const data = new Uint8Array(nx * ny);
  const rows: number[][] = [];
  for (const ring of rings) {
    const touched: number[] = [];
    for (let i = 0, k = ring.length - 1; i < ring.length; k = i++) {
      const a = ring[i]!;
      const b = ring[k]!;
      if (a.y === b.y) continue;
      const lo = Math.min(a.y, b.y);
      const hi = Math.max(a.y, b.y);
      // rows whose centre yc satisfies lo <= yc < hi (half-open, so shared vertices count once)
      const j0 = Math.max(0, Math.ceil((lo - y0) / cell - 0.5));
      const j1 = Math.min(ny - 1, Math.ceil((hi - y0) / cell - 0.5) - 1);
      const dxdy = (b.x - a.x) / (b.y - a.y);
      for (let j = j0; j <= j1; j++) {
        const yc = y0 + (j + 0.5) * cell;
        let row = rows[j];
        if (!row) {
          row = rows[j] = [];
        }
        if (row.length === 0) touched.push(j);
        row.push(a.x + (yc - a.y) * dxdy);
      }
    }
    for (const j of touched) {
      const xs = rows[j]!;
      xs.sort((p, q) => p - q);
      const base = j * nx;
      for (let m = 0; m + 1 < xs.length; m += 2) {
        const i0 = Math.max(0, Math.ceil((xs[m]! - x0) / cell - 0.5));
        const i1 = Math.min(nx - 1, Math.floor((xs[m + 1]! - x0) / cell - 0.5));
        for (let i = i0; i <= i1; i++) data[base + i] = 1;
      }
      xs.length = 0;
    }
  }
  return { x0, y0, cell, nx, ny, data };
}

export function covered(r: CoverageRaster, x: number, y: number): boolean {
  const i = Math.floor((x - r.x0) / r.cell);
  const j = Math.floor((y - r.y0) / r.cell);
  if (i < 0 || j < 0 || i >= r.nx || j >= r.ny) return false;
  return r.data[j * r.nx + i] === 1;
}

/**
 * Copper at or around a point (ring of radius r). Vias sit in anti-pad holes of the planes
 * they pass, but the plane is right there for their return current.
 */
export function coveredNear(rs: CoverageRaster, x: number, y: number, r = 0.8): boolean {
  if (covered(rs, x, y)) return true;
  for (let k = 0; k < 8; k++) {
    const a = (k * Math.PI) / 4;
    if (covered(rs, x + r * Math.cos(a), y + r * Math.sin(a))) return true;
  }
  return false;
}

/**
 * Reference copper for return currents. Plane rasters have their small holes filled
 * (fillSmallHoles), so via anti-pads count as copper while slots and cut-outs do not.
 */
export function referenceCopper(rs: CoverageRaster, x: number, y: number): boolean {
  return covered(rs, x, y);
}

/**
 * Fill enclosed holes whose largest extent is at most maxExtent (mm): via anti-pads and
 * thermal gaps carry no return-current detour worth modelling; slots (long, however narrow)
 * and cut-outs (larger, or open to the board edge) stay. 4-connected flood fill over the
 * uncovered cells.
 */
export function fillSmallHoles(r: CoverageRaster, maxExtent: number): CoverageRaster {
  const { nx, ny } = r;
  const data = r.data.slice();
  const seen = new Uint8Array(nx * ny);
  const maxCells = maxExtent / r.cell;
  const stack: number[] = [];
  const comp: number[] = [];
  for (let start = 0; start < nx * ny; start++) {
    if (data[start] || seen[start]) continue;
    stack.length = 0;
    comp.length = 0;
    stack.push(start);
    seen[start] = 1;
    let open = false;
    let i0 = nx;
    let i1 = -1;
    let j0 = ny;
    let j1 = -1;
    while (stack.length) {
      const v = stack.pop()!;
      comp.push(v);
      const i = v % nx;
      const j = (v - i) / nx;
      if (i < i0) i0 = i;
      if (i > i1) i1 = i;
      if (j < j0) j0 = j;
      if (j > j1) j1 = j;
      if (i === 0 || j === 0 || i === nx - 1 || j === ny - 1) open = true;
      const nb = [i > 0 ? v - 1 : -1, i < nx - 1 ? v + 1 : -1, j > 0 ? v - nx : -1, j < ny - 1 ? v + nx : -1];
      for (const u of nb) {
        if (u < 0 || data[u] || seen[u]) continue;
        seen[u] = 1;
        stack.push(u);
      }
    }
    if (!open && Math.max(i1 - i0 + 1, j1 - j0 + 1) <= maxCells) for (const v of comp) data[v] = 1;
  }
  return { ...r, data };
}
